import React, { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);

import Scrollspy from 'react-scrollspy';

const Engineering = () => {
  useEffect(() => {
    
  
  }, [])
  
  return (
    <div className="simple_page">
      <div className="mainIntro">
        <h1>Engineering</h1>
        <h3>Cure disease - Secure generations!</h3>
      </div>
      <section className="divider">
        <div className="fixed_sidebar">
          <div className="sidebar">
            <div className="main_sidebar">
              ENGINEERING
            </div>
              <Scrollspy
              className="scrollspy" items={['Cycle1', 'Cycle2']}
                currentClassName="active">
              <li><a href="#Cycle1">Cycle 1</a></li>
              <li><a href="#Cycle2">Cycle 2</a></li>
              </Scrollspy>
              
          </div>

        </div>
        <div className="main_lab_style">
          <>
            
            <p id="ENGINEERING">
              <h1>ENGINEERING</h1>
            </p>
            <p>
              We utilized the Design-Build-Test-Learn-Repeat cycle to improve the platform over several iterations.
            </p>
            <br />
            <section id="Cycle1">
              <p >
                  <h1>Cycle 1: </h1>
                  <h3>Engineer the new biosynthetic pathway</h3>
              </p>
              <p>
                  <strong>Design:</strong> Search for biosynthetic gene clusters with similar products.
                  <br />
                <strong>Build:</strong> Replacing the SDR gene in the SAD biosynthetic pathway by heterologous expression.
                  <br />
                <strong>Test:</strong> High Performance Liquid Chromatography-Mass Spectrometry (HPLC-MS) analysis and the production of secalonic acid analogues.
                  <br />
                <strong>Learn:</strong> Structure elucidation of the secalonic acid analogues by spectroscopic analysis.
              </p>
              <br />
              <div className="diagram m-4" style={{marginTop:50}}>
                  <div className="row d-flex justify-content-between align-center">
                    <div className="circle floating">
                      DESIGN
                    </div>
                    <div className="triangle">
                      <div class="arrow-right"></div>
                    </div>
                    <div className="circle floating floating2">
                      BUILD
                    </div>
                  </div>
                  <div className="row d-flex justify-content-between align-center">
                    <div class="arrow-up" style={{ marginLeft: 75 }}></div>
                    <div className="text">
                      iGEM
                      <br />
                      Engineering
                      <br /> cycle
                    </div>
                    <div class="arrow-down" style={{marginRight:55}}></div>
                  </div>
                  <div className="row d-flex justify-content-between align-center">
                    <div className="circle floating floating3">
                      LEARN
                    </div>
                    <div className="triangle">
                      <div class="arrow-left"></div>
                    </div>
                    <div className="circle floating floating4">
                      TEST
                    </div>
                  </div>
              </div>

              <br />
              <br />
              <p><strong>Design: Search for biosynthetic gene clusters with similar products. </strong></p>
              <p><strong>In this project, we are focusing on one class of natural product (NP), tetrahydroxanthone (THX) dimer. </strong><u>THX dimers</u> often carry interesting bioactivity such as <u>antibacterial and anticancer properties</u>. In previous studies conducted by Matsuda and his colleagues, the biosynthetic pathway of two bioactive THX dimers, secalonic acid D (SAD) and neosartorin, was characterized with heterologous expression and gene deletion experiment. The structure of SAD and neosartorin is similar as they are both originated from blennolide monomers. The genes involved in the biosynthetic pathway are also similar to each other (<strong>Figure 1</strong>). In both SAD and neosartorin cluster, the intermediate will undergo a stereoselective ketoreduction to yield the monomeric blennolides. This reaction is carried out by a short-chain dehydrogenase/reductase (SDR; AacuF in the SAD pathway and NsrO in the neosartorin pathway). <u>Sequence alignment shows that the two enzymes (AacuF and NsrO) share a 50% similarity.</u></p>
              <p><strong>Therefore, we decided to work on these two clusters and try to apply synthetic biology to replace the SDR gene in the SAD biosynthetic cluster.</strong></p>
              <p><strong>Figure 1</strong>. Schematic representation of the SAD and neosartorin pathway. Arrows with same colour represent similar genes in both pathways</p>
              <img src="https://static.igem.wiki/teams/4154/wiki/assets/image-6.png" alt="" />

              <p><strong>Build: Replacing the SDR gene in the SAD biosynthetic pathway by heterologous expression. </strong></p>
              <p>Initially, <em>nsrO </em>and <em>aacuE</em> were amplified from pTAex3-based plasmids, previously constructed by Matsuda and his colleagues, using polymerase chain reaction (PCR). The gene fragments obtained herein are under the control of an &alpha;-amylase promoter (PamyB). The two gene fragments, along with <em>nsrQ</em>, were then cloned into a pUNA-HR vector using an In-Fusion Snap Assembly Kit (Takara Bio Inc.) to yield pUNA-HR-nsrQOE. The plasmid was transformed into an <em>Aspergillus oryzae </em>host (NSARI/nsrABCDERJIGKaacuH), which harbors all the precursor genes producing the substrates of NsrO, with a CRISPR-Cas9-mediated transformation protocol according to Wei et al. (2021).</p>
              <p>As the original <em>A. oryzae </em>host does not carry the nitrate reductase gene, it cannot uptake nitrate ion as nitrogen source effectively. Therefore, the fungal colony will appear in a starvation morphology. The nitrate reductase on the pUNA-HR vector would compensate the nutrient auxotrophy, hence selection can be performed in Czapek-Dox (CD) medium containing sodium nitrate as the only nitrogen source. The successful transformants were subsequently transferred to a Dextrin-Peptone-Yeast extract (DPY) medium to induce the expression of the gene under the control of the PamyB promoter.</p>
<br />
              <p><h3>Test: High Performance Liquid Chromatography-Mass Spectrometry (HPLC-MS) analysis and the production of secalonic acid analogues. </h3></p>
              <p>The DPY agar plate containing the transformed fungal colony was cut out and the metabolites were extracted with ethyl acetate. The metabolic profile of the fungal colony was checked by UPLC. In one of the colonies, five new peaks with a m/z at 639 were detected in which they are not observed in the original host. As we expect the new natural products will be the analogue of SAD which has a <em>m/z</em> 639 [M+H]<sup>+</sup> (Calcd for C<sub>32</sub>H<sub>31</sub>O<sub>14</sub>), we believe the five peaks are corresponding to five SAD analogues produced by NsrO.</p>
              <p>The successful colony was then cultivated in 2L of DPY medium for 7 days at 30 &deg;C. The metabolites were extracted with ethyl acetate and yielded 1.78 g of crude extract. Then the crude extract was subjected to flash chromatography (Isolera Spektra One flash purification system; Biotage) and eluted stepwise using a dichloromethane:ethyl acetate gradient (100:0 to 0:100). Fractions containing secalonic acid analogues were collected and further purified with reverse-phase preparative HPLC (50% aqueous acetonitrile with 0.05% TFA, 10 mL/min) to yield 19.3 mg of <strong>1</strong> and 4.7 mg of <strong>2</strong>. Unfortunately, the remaining three analogues were produced at a very low yield and hence cannot be isolated for characterization.</p>
              
<br />
              <p><h3>Learn: Structure elucidation of the secalonic acid analogues by spectroscopic analysis. </h3></p>
              
              <p>The structures of <strong>1</strong> and <strong>2</strong> were elucidated with nuclear magnetic resonance (NMR) and circular dichroism (CD) spectroscopy. Briefly, both 1D and 2D NMR spectra were used to establish the planar structure of the molecule. Furthermore, the relative configurations of all five stereocenters and the axial chirality between two benzoyl chromophores were determined by NOESY spectra and CD spectra, respectively.&nbsp;</p>
              <p>
                <img src="https://static.igem.wiki/teams/4154/wiki/assets/123443122341234.jpeg" alt="" />
                <div>
                  Figure 2. Structures of the two secalonic acid analogues.
                </div>
              </p>
            </section>
            
            
            
            

            <section id="Cycle2">
              <p >
                <h1>Cycle 2: </h1>
                <h3>Biosynthesis of new tetrahydroxanthone dimer</h3>
              </p>
              <p>
                <strong>Design:</strong> Search for possible anticancer targets.
                <br />
                <strong>Build:</strong> in vitro  validation of the compounds’ anticancer properties.
                <br />
                <strong>Test:</strong>: in silico modeling interactions between human topoisomerase I and the novel secalonic acid analogs.
                <br />
                <strong>Learn:</strong> Reveal the efficacy against colon cancer.
              </p>
              <br />
              <div className="diagram m-4" style={{ marginTop: 50, marginBottom:100 }}>
                <div className="row d-flex">
                  <div className="romb">
                    <div className="circle ">
                      BUILD
                    </div>
                    <div className="circle ">
                      TEST
                    </div>
                  </div>
                  <div className="text">
                    iGEM
                    <br />
                    Engineering
                    <br /> cycle
                  </div>
                  <div className="oval">
                    
                  </div>
                  <div className="romb">
                    <div className="circle ">
                      LEARN
                    </div>
                    <div className="circle ">
                      DESIGN
                    </div>
                  </div>
                  
                </div>
                
              </div>
              <br />

              <p><h3>Design: Search for possible anticancer targets.</h3></p>
              <p>The new biosynthetic pathway is based on the one producing secalonic acid D (SAD), a known antitumor agent. Thus, the two novel tetrahydroxanthone (THX) dimers with similar structures to SAD were expected to exhibit similar biological activities. Some previous studies indicated that the SAD act as the topoisomerase I inhibitor to exhibit cytotoxicity (Hong, 2011). This evidence provides a rational prediction that the two novel compounds would also exhibit cytotoxicity. <strong>Therefore, we decided to test the anticancer activity of the novel compounds via in vitro  bioassay.</strong></p>
<br />
              <p><h3>Build: in vitro validation of the compounds&rsquo; anticancer properties.</h3></p>
              <p>The anticancer activity of the two novel compounds (1 and 2) is conducted in vitro with MTT assays to investigate the IC50. The two cell lines: the human colorectal adenocarcinoma cell line HT29 and the human ovarian cancer cell line A780, were used.</p>
              <p>Previous studies expected that the blennolides do not exhibit antitumor activity, whereas the THX dimer is bioactive. The MTT result indicated that the novel THX dimers have lower IC50 toward the HT29 and A2780, whereas the corresponding blennolides with larger IC50 are non-bioactive. The significant difference in IC50 results between the novel THX dimer and their blennolides proved our expectation. Notably, compound 1 with a lower IC50 level is more effective as the antitumor agent.</p>
              
              <br />
              <p><h3>Test: in silico modeling interactions between human topoisomerase I and the novel secalonic acid analogs.</h3></p>
              <p>To explore the mechanism of action, the AutoDock Vina software was applied to analyze the molecular docking of two novel THX dimers with topoisomerase I enzyme. Surprisingly, the novel compounds have different action mechanisms with SAD and are more similar to camptothecin, another antitumor agent. Moreover, we found that compound 1 has higher activity than compound 2 due to the stronger hydrogen bond formation with the target site.</p>
              
              <br />
              <p><h3>Learn: Reveal the efficacy against colon cancer.</h3></p>
              <p>Our project worked on discovering new medical natural products. During the web-lab experiments, two novel tetrahydroxanthone (THX) dimers have been successfully produced via new biosynthetic pathway construction. The two THX dimers have shown antitumor activity in the in-vitro and in-vivo testing, and their mechanism of action were studied with the aid of AutoDock Vina software. They are determined as topoisomerase I inhibitors, as predicted. Surprisingly, the novel THX dimers have a similar mechanism as camptothecin (CPT) rather than SAD.</p>

              <p><strong>References:</strong></p>
              <p>Hong, R. (2011). Secalonic acid D as a novel DNA topoisomerase I inhibitor from marine lichen-derived fungus Gliocladium sp. T31. Pharmaceutical Biology, 49(8), 796-799.</p>
              
            </section>
            
          </>
        </div>
      </section>
    </div>
  );
}

export default Engineering;
